import { baseUrl, HtmlReactParser, translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { StyledChannels, StyledContentContainer, StyledDescription, StyledTitle } from "./styled-components";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { Truncate } from "@kaltura/mediaspace-shared-ui";
import { ChannelsGrid, ChannelsContext, FiltersSectionProps } from "@kaltura/mediaspace-shared-channels-grid";
import { FilterValues } from "@kaltura/mediaspace-shared-types";
import { useMemo } from "react";
import { CreateCategoryModalContentsProps } from "@kaltura/mediaspace-shared-create-category-modal";

export interface ChannelsProps extends FiltersSectionProps {
    /**
     * channel description
     */
    description?: string;

    /**
     * pagination page size
     */
    pageSize: number;

    /**
     * initial filter values
     */
    initialValues?: FilterValues;

    /**
     * subscribe url
     */
    subscribeUrl: string;

    /**
     * unsubscribe url
     */
    unsubscribeUrl: string;

    /**
     * should show create channel button
     */
    allowCreateChannel: boolean;

    /**
     * create channel modal props
     */
    createChannelModalProps?: CreateCategoryModalContentsProps;
}

const Channels = (props: ChannelsProps) => {
    const {
        description,
        pageSize,
        subscribeUrl,
        unsubscribeUrl,
        allowCreateChannel,
        createChannelModalProps,
        ...filtersProps
    } = props;

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

    const channelsContextValue = useMemo(
        () => ({
            pageSize,
            subscribeUrl,
            unsubscribeUrl,
            allowCreateChannel,
            createChannelModalProps,
            searchUrl: `${baseUrl}/default/channels/search-results`,
        }),
        [pageSize, subscribeUrl, unsubscribeUrl, allowCreateChannel, createChannelModalProps]
    );

    return (
        <ChannelsContext.Provider value={channelsContextValue}>
            <StyledChannels>
                <StyledTitle variant={isSmallScreen ? "h2" : "h1"} component="h1">
                    {translate("Channels")}
                </StyledTitle>

                {description && (
                    <StyledDescription variant="body1" component="div">
                        <Truncate
                            lines={3}
                            showMoreTranslatedText={translate("Read more")}
                            showLessTranslatedText={translate("Read less")}
                        >
                            {HtmlReactParser(description)}
                        </Truncate>
                    </StyledDescription>
                )}

                <StyledContentContainer>
                    <ChannelsGrid {...filtersProps} />
                </StyledContentContainer>
            </StyledChannels>
        </ChannelsContext.Provider>
    );
};

export default Channels;
